import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, Image, Save, CheckCircle, Clock, X, Edit3, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert } from '../design-system';
import AuthenticatedImage from '../design-system/AuthenticatedImage';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const TaskPhotoJournal = ({ task, session, onComplete }) => {
  const { userInfo } = useAuth();
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const [thoughts, setThoughts] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Auto-save functionality
  const saveProgress = useCallback(async (thoughtsText, photoData) => {
    if (!session?.sessionId) return;

    setIsSaving(true);
    try {
      await apiPost(
        '/.netlify/functions/photoJournalTaskApi',
        {
          sessionId: session.sessionId,
          thoughts: thoughtsText,
          photoId: photoData?.photoId || photoId,
          photoUrl: photoData?.url || photoUrl
        },
        userInfo?.userId
      );

      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, userInfo?.userId, photoId, photoUrl]);

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (thoughtsText) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(thoughtsText, { photoId, url: photoUrl });
        }, 10000); // 10 seconds
      };
    })(),
    [saveProgress, photoId, photoUrl]
  );

  // Load progress from session
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing data if session has it
    if (session.sessionData?.thoughts) {
      setThoughts(session.sessionData.thoughts);
    }
    if (session.sessionData?.photoUrl) {
      setPhotoUrl(session.sessionData.photoUrl);
      setPhotoId(session.sessionData.photoId);
    }
  }, [session, hasLoadedProgress]);

  const handleThoughtsChange = (value) => {
    setThoughts(value);
    debouncedSave(value);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    setPhoto(file);
    setIsUploading(true);

    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Upload photo
      const response = await apiPost(
        '/.netlify/functions/upload-task-photo',
        {
          sessionId: session.sessionId,
          fileData: base64,
          fileName: file.name,
          mimeType: file.type,
          size: file.size
        },
        userInfo?.userId
      );

      if (response.photo) {
        setPhotoUrl(response.photo.url);
        setPhotoId(response.photo.photoId);
        toast.success('Photo uploaded successfully');
        
        // Save progress with photo
        await saveProgress(thoughts, response.photo);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.message || 'Failed to upload photo');
      setPhoto(null);
    } finally {
      setIsUploading(false);
      // Reset file inputs
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    if (!photoId) return;

    try {
      await apiPost(
        '/.netlify/functions/delete-task-photo',
        {
          sessionId: session.sessionId,
          photoId: photoId
        },
        userInfo?.userId
      );

      setPhoto(null);
      setPhotoUrl(null);
      setPhotoId(null);
      toast.success('Photo removed');
    } catch (error) {
      console.error('Error removing photo:', error);
      toast.error('Failed to remove photo');
    }
  };

  const handleManualSave = async () => {
    await saveProgress(thoughts, { photoId, url: photoUrl });
    toast.success('Progress saved!');
  };

  const handleComplete = async () => {
    if (!photoUrl && !thoughts.trim()) {
      toast.error('Please add a photo or write your thoughts before completing');
      return;
    }

    setIsSubmitting(true);
    try {
      const completionData = {
        photoId: photoId,
        photoUrl: photoUrl,
        thoughts: thoughts.trim(),
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/photoJournalTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        userInfo?.userId
      );

      toast.success('Photo journal completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto px-3 md:px-4 py-4">
      <Card variant="elevated" padding="medium" className="h-full flex flex-col overflow-hidden">
        {/* Task Header */}
        <div className="mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Camera className="h-5 w-5 text-purple-600" />
            </div>
            <h1 className="text-base md:text-lg font-semibold text-gray-900 leading-relaxed">
              {task.title || task.description}
            </h1>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0 mb-4">
          {/* Photo Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo
            </label>
            
            {photoUrl ? (
              <div className="relative bg-gray-50 rounded-lg p-2">
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                  <AuthenticatedImage 
                    src={photoUrl} 
                    alt="Uploaded photo" 
                    className="w-full h-auto max-h-96 object-contain"
                  />
                </div>
                <Button
                  onClick={handleRemovePhoto}
                  variant="ghost"
                  size="small"
                  className="absolute top-4 right-4 bg-white hover:bg-gray-100 border border-gray-300 shadow-sm"
                  title="Remove photo"
                >
                  <X className="h-4 w-4 text-gray-700" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Camera className="h-6 w-6 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Take a photo or upload an image</p>
                    <div className="flex gap-2 justify-center">
                      <Button
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={isUploading}
                        variant="outline"
                        size="small"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Take Photo
                      </Button>
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        variant="outline"
                        size="small"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Hidden file inputs */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            )}
            
            {isUploading && (
              <div className="mt-2 text-sm text-gray-500 text-center">
                Uploading photo...
              </div>
            )}
          </div>

          {/* Thoughts Input Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Your Thoughts
              </label>
              {lastSaved && (
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="h-3 w-3 mr-1" />
                  Last saved: {lastSaved.toLocaleTimeString()}
                </div>
              )}
            </div>
            <textarea
              id="thoughts"
              value={thoughts}
              onChange={(e) => handleThoughtsChange(e.target.value)}
              placeholder="Write your thoughts about the photo..."
              className="w-full h-48 resize-none rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>

          {/* Helpful Tips */}
          <Alert variant="info">
            <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
              <span className="mr-2">💡</span>
              Tips
            </h3>
            <ul className="text-sm text-blue-700 space-y-1.5">
              <li>• Take or upload a photo that represents what you want to share</li>
              <li>• Write freely about your thoughts and feelings related to the photo</li>
              <li>• Your progress is automatically saved every 10 seconds</li>
            </ul>
          </Alert>
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleManualSave}
            disabled={isSaving}
            variant="ghost"
            size="medium"
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          <Button
            onClick={handleComplete}
            disabled={isSubmitting || (!photoUrl && !thoughts.trim())}
            variant="primary"
            size="medium"
            className="flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Completing...' : 'Complete Task'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Summary for patient dashboard
TaskPhotoJournal.Summary = ({ task, session }) => {
  const hasPhoto = session?.sessionData?.photoUrl;
  const hasThoughts = session?.sessionData?.thoughts;
  const thoughtsLength = hasThoughts ? session.sessionData.thoughts.length : 0;
  
  return (
    <div className="photo-journal-summary">
      <div className="flex items-center space-x-2">
        <Camera className="h-4 w-4 text-purple-600" />
        <span className="text-xs text-gray-500">
          {hasPhoto && hasThoughts 
            ? `Photo uploaded, ${thoughtsLength} characters written`
            : hasPhoto 
            ? 'Photo uploaded'
            : hasThoughts
            ? `${thoughtsLength} characters written`
            : 'Not started'}
        </span>
      </div>
    </div>
  );
};

// Results summary for therapist view
TaskPhotoJournal.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData) return null;

  return (
    <Card variant="elevated" padding="large">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">Photo Journal Results</h4>
      
      <div className="flex flex-col md:flex-row items-start space-y-1 md:space-y-0 md:space-x-2 mb-1">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Camera className="h-5 w-5 text-purple-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="text-lg font-medium text-gray-900">{task.title}</h5>
          <p className="text-sm text-gray-500">
            Completed by {patientName} on {new Date(completionData.completedAt || Date.now()).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Photo Display */}
      {completionData.photoUrl && (
        <div className="border-t pt-1 mb-1">
          <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Image className="h-4 w-4 mr-1" />
            Patient's Photo:
          </h6>
          <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
            <AuthenticatedImage 
              src={completionData.photoUrl} 
              alt="Patient's photo" 
              className="w-full h-auto max-h-96 object-contain"
            />
          </div>
        </div>
      )}

      {/* Thoughts Display */}
      {completionData.thoughts && (
        <div className="border-t pt-1">
          <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Edit3 className="h-4 w-4 mr-1" />
            Patient's Thoughts:
          </h6>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {completionData.thoughts}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

// Task assignment form for therapist
TaskPhotoJournal.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  return (
    <Card variant="default" padding="large">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instruction for Patient *
          </label>
          <Input
            type="textarea"
            value={formData.instruction || ''}
            onChange={(e) => setFormData(prev => ({...prev, instruction: e.target.value}))}
            placeholder="Enter instructions for the patient (e.g., 'Take a photo of something that brings you joy and write about it')..."
            rows={3}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            This instruction will guide the patient on what photo to take and what to write about.
          </p>
        </div>
      </div>
    </Card>
  );
};

export default TaskPhotoJournal;

